import { getAppConfig } from "@lib/config";
import { getUserEntities, getUserStatus, getUserWorkspaces } from "@lib/user";
import {
    getActiveWorkspaceKeyForUserId,
    useWorkspaceContext,
} from "@state/workspaceContext";
import { useCallback, useState } from "react";
import { UserStatusResponse } from "@common/dto/onboarding/user-status-response.dto";
import { queryClient } from "queryClient";
import * as Sentry from "@sentry/react";
import { getEnv } from "@lib/environment";
import { intercom } from "@lib/intercom";
import { ACTIVE_USER_ID_KEY } from "@constants";
import { getEntitiesAccountsQueryKey } from "@queries/entitiesAccounts";
import { UserEntitiesResponse } from "@common/dto/onboarding/user-entities-response.dto";
import { UserWorkspacesResponse } from "@common/dto/onboarding/user-workspaces-response.dto";
import { Workspace } from "@common/types/workspace";
import { workspacesQueryKeys } from "./useWorkspaces";
import { userStatusQueryKey } from "./useUserStatus";
import { appConfigQueryKeys } from "./useAppConfig";
import { useTypedFlags } from "./useTypedFlags";

export function useLoginInitializer() {
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const { activeWorkspaceId, changeActiveWorkspace } = useWorkspaceContext();
    const { personalView: isPersonalViewEnabled } = useTypedFlags();

    const handleInitSuccess = useCallback(
        (
            userStatus: UserStatusResponse,
            workspacesWithEntities: UserWorkspacesResponse | void,
        ) => {
            const user = userStatus.user;

            (window as any).dataLayer?.push({
                event: "userFetched",
                userId: user.id,
            });

            queryClient.setQueryData([userStatusQueryKey], userStatus);
            queryClient.setQueryData(
                workspacesQueryKeys.withEntities(false),
                workspacesWithEntities,
            );

            Sentry.setUser({ email: user.email ?? undefined });
            localStorage.setItem(ACTIVE_USER_ID_KEY, user.id.toString());

            if (getEnv("VITE_INTERCOM_APP_ID") && userStatus.intercomHash) {
                intercom("boot", {
                    api_base: "https://api-iam.intercom.io",
                    app_id: getEnv("VITE_INTERCOM_APP_ID"),
                    name: user.name,
                    email: user.email, // Email address
                    user_hash: userStatus.intercomHash, // HMAC using SHA-256
                });
            }
        },
        [],
    );

    const handleInitFail = useCallback(() => {
        queryClient.setQueryData([userStatusQueryKey], undefined);
        queryClient.setQueryData(workspacesQueryKeys.all(), undefined);

        localStorage.removeItem(ACTIVE_USER_ID_KEY);
        Sentry.getCurrentScope().setUser(null);
    }, []);

    const handleActiveWorkspace = useCallback(
        async (workspace: Workspace | null) => {
            changeActiveWorkspace(workspace);

            if (!workspace) {
                return;
            }

            const entities = await getUserEntities(workspace.id);

            if (entities) {
                queryClient.setQueryData(
                    getEntitiesAccountsQueryKey(workspace.id),
                    (current: UserEntitiesResponse | undefined) => ({
                        ...(current ?? {}),
                        entities: entities.entities,
                    }),
                );
            }
        },
        [changeActiveWorkspace],
    );

    const initialize = useCallback(
        async (isLogin: boolean) => {
            try {
                const [userStatus, workspaces, appConfig] = await Promise.all([
                    getUserStatus(),
                    getUserWorkspaces(false),
                    getAppConfig(),
                ]);

                queryClient.setQueryData(appConfigQueryKeys.all(), appConfig);

                if (userStatus && workspaces) {
                    handleInitSuccess(userStatus, workspaces);
                } else {
                    handleInitFail();
                    return;
                }

                // user has access to a single workspace, set it to active
                if (workspaces.workspaces.length === 1) {
                    const onlyWorkspace = workspaces.workspaces[0];
                    handleActiveWorkspace(onlyWorkspace);

                    return;
                }

                if (!userStatus.user.onboardingComplete) {
                    const firstOnboardedWorkspace = workspaces.workspaces.find(
                        (workspace) => workspace.onboardingCompletedAt,
                    );

                    handleActiveWorkspace(
                        firstOnboardedWorkspace ?? workspaces.workspaces[0],
                    );
                    return;
                }

                if (isLogin && isPersonalViewEnabled) {
                    handleActiveWorkspace(null);
                    return;
                }

                const teamKey =
                    activeWorkspaceId ??
                    (userStatus
                        ? localStorage.getItem(
                              getActiveWorkspaceKeyForUserId(
                                  userStatus.user.id,
                              ),
                          )
                        : null);

                const activeWorkspace = workspaces.workspaces.find(
                    (workspace) => workspace.id === teamKey,
                );

                if (activeWorkspace) {
                    handleActiveWorkspace(activeWorkspace);
                    return;
                }

                if (isPersonalViewEnabled) {
                    handleActiveWorkspace(null);
                    return;
                }

                // don't know which workspace to set as active, set the first one
                if (workspaces.workspaces.length) {
                    const firstWorkspace = workspaces.workspaces[0];
                    handleActiveWorkspace(firstWorkspace);

                    return;
                }

                handleActiveWorkspace(null);
            } finally {
                setIsInitialized(true);
            }
        },
        [
            handleInitSuccess,
            handleInitFail,
            handleActiveWorkspace,
            activeWorkspaceId,
            isPersonalViewEnabled,
        ],
    );

    return {
        isInitialized,
        initialize,
    };
}
